import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Checkbox,
    FormControlLabel,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    InputAdornment,
    Divider,
    Typography,
    Box
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business'; // Icon giả lập cho Nhóm
import FacebookIcon from '@mui/icons-material/Facebook'; // Icon giả lập cho Page
import Swal from 'sweetalert2';

export default function ApplyFanpageDialog({ open, handleClose, aiData, listFanpages, listGroups }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedIds, setSelectedIds] = useState([]); // Lưu ID dạng chuỗi
    const [loading, setLoading] = useState(false);

    // Gộp danh sách Page và Group để hiển thị chung trong 1 dialog
    const combinedList = useMemo(() => {
        const pages = (listFanpages || []).map(p => ({ ...p, id: String(p.fanpage_id || p.id), type: 'page' }));
        const groups = (listGroups || []).map(g => ({ ...g, id: String(g.id), type: 'group' }));
        return [...pages, ...groups];
    }, [listFanpages, listGroups]);

    // Khởi tạo các ID đã được áp dụng trước đó (nếu có)
    useEffect(() => {
        if (open && aiData) {
            // Giả sử aiData có mảng fanpage_ids và group_ids
            const preSelected = [
                ...(aiData.fanpage_ids || []).map(id => String(id)),
                ...(aiData.group_ids || []) .map(id => String(id))
            ];
            setSelectedIds(preSelected);
            setSearchTerm("");
        }
    }, [open, aiData]);

    // Logic tìm kiếm
    const filteredItems = useMemo(() => {
        return combinedList.filter(item => 
            item.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
            item.id.includes(searchTerm)
        );
    }, [combinedList, searchTerm]);

    const handleToggle = (id) => {
        const idStr = String(id);
        setSelectedIds(prev => 
            prev.includes(idStr) ? prev.filter(i => i !== idStr) : [...prev, idStr]
        );
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const filteredIds = filteredItems.map(i => i.id);
            const newSelected = Array.from(new Set([...selectedIds, ...filteredIds]));
            setSelectedIds(newSelected);
        } else {
            const filteredIds = filteredItems.map(i => i.id);
            setSelectedIds(selectedIds.filter(id => !filteredIds.includes(id)));
        }
    };

    const isAllSelected = filteredItems.length > 0 && 
        filteredItems.every(i => selectedIds.includes(i.id));
    
    const isIndeterminate = !isAllSelected && 
        filteredItems.some(i => selectedIds.includes(i.id));

    const handleConfirm = async () => {
        setLoading(true);
        try {
            // Giả lập gọi API gửi mảng selectedIds
            console.log("Gửi danh sách áp dụng cho AI ID:", aiData.id, selectedIds);
            
            // await fetch(api.apply_ai_to_pages, { ... });
            
            setLoading(false);
            Swal.fire("Thành công", "Đã cập nhật danh sách áp dụng cho AI", "success");
            handleClose();
        } catch (error) {
            setLoading(false);
            Swal.fire("Lỗi", "Không thể cập nhật dữ liệu", "error");
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Áp dụng AI cho Fanpage & Nhóm</Typography>
                    <Typography variant="caption" color="text.secondary">AI đang chọn: {aiData?.name}</Typography>
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox 
                            checked={isAllSelected}
                            indeterminate={isIndeterminate}
                            onChange={handleSelectAll}
                            disabled={loading}
                        />
                    }
                    label="Chọn tất cả (đang lọc)"
                />
            </DialogTitle>
            <Divider />
            <DialogContent>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Tìm kiếm tên Fanpage hoặc Nhóm..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={loading}
                    sx={{ mb: 2, mt: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />

                <Grid container spacing={2}>
                    {filteredItems.map((item) => {
                        const isChecked = selectedIds.includes(item.id);
                        return (
                            <Grid item xs={12} sm={6} key={item.id}>
                                <ListItem
                                    button
                                    onClick={() => handleToggle(item.id)}
                                    disabled={loading}
                                    sx={{ 
                                        border: "1px solid #e0e0e0", 
                                        borderRadius: "8px",
                                        transition: '0.2s',
                                        '&:hover': { bgcolor: '#f5f9ff', borderColor: '#1976d2' }
                                    }}
                                >
                                    <Checkbox checked={isChecked} disabled={loading} edge="start" />
                                    <ListItemAvatar>
                                        <Avatar 
                                            src={item.avatar} 
                                            sx={{ bgcolor: item.type === 'page' ? '#e3f2fd' : '#f3e5f5' }}
                                        >
                                            {item.type === 'page' ? 
                                                <FacebookIcon sx={{ color: '#1877f2' }} /> : 
                                                <BusinessIcon sx={{ color: '#9c27b0' }} />
                                            }
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText 
                                        primary={item.name} 
                                        secondary={item.type === 'page' ? `ID: ${item.id}` : `Nhóm: ${item.id}`}
                                        primaryTypographyProps={{ fontWeight: isChecked ? 'bold' : 'normal', noWrap: true }}
                                    />
                                </ListItem>
                            </Grid>
                        );
                    })}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                <Button onClick={handleClose} disabled={loading} color="inherit">Hủy</Button>
                <LoadingButton
                    loading={loading}
                    variant="contained"
                    onClick={handleConfirm}
                    color="primary"
                    sx={{ px: 4 }}
                >
                    Xác nhận áp dụng ({selectedIds.length})
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}